import { get } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

/**
 * @type {class}
 * @desc RxEnableAutoRefill defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { RxEnableAutoRefillService } from 'services/rxEnableAutoRefill';
 *
 * // create service instance and send/cancel request
 * const rxEnableAutoRefillService = new RxEnableAutoRefillService();
 * const result = await rxEnableAutoRefillService.fetchData( '<user-id>' ); // fetch data
 * rxEnableAutoRefillService.cancel(); // cancel an active request
 */
export class RxEnableAutoRefillService extends HttpService {

    // make request and fetch component data
    async fetchComponentData( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const componentData = get( response, 'body.component', {} );
            const visibility = get( componentData, 'visibility', {} );

            // create an array of visible `items` for easy accessibility
            // (`items` that are not part of any other array that can be used in getVisibleItems)
            const visibleItems = getVisibleItems( [
                get( componentData, 'structure.savedAddresses', {} ),
                get( componentData, 'structure.savedAddresses.addAction', {} ),
                get( componentData, 'structure.savedAddresses.infoCard', {} ),
                get( componentData, 'structure.paymentOptions', {} ),
                get( componentData, 'structure.paymentOptions.addAction', {} ),
                get( componentData, 'structure.paymentOptions.infoCard', {} ),
                get( componentData, 'structure.phoneNumber', {} ),
                get( componentData, 'structure.phoneNumber.addAction', {} ),
                get( componentData, 'structure.phoneNumber.infoCard', {} ),
                get( componentData, 'structure.checkboxInfo', {} ),
                get( componentData, 'structure.patientAutorefillConsent', {} ),
                get( componentData, 'structure.patientAutorefillConsent.infoCard', {} ),
                get( componentData, 'structure.attestationProvider', {} )
            ], visibility );
            
            
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    data: get( componentData, 'data', {} ),
                    title: get( componentData, 'structure.title' ),
                    subtitle: get( componentData, 'structure.subtitle' ),
                    primaryColumns: getVisibleItems( get( componentData, 'structure.primaryColumns', [] ), visibility ),
                    widgetInfoArray: getVisibleItems( get( componentData, 'structure.widgetInfo', [] ), visibility ),
                    actions: get( componentData, 'structure.actions', [] ),
                    visibleItems
                },
            };
            
        } catch( errResponse ) {
            return errResponse;
        }
    }

    // submit new address
    async submitNewAddress( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE )
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // submit enable auto refill request
    async enableAutoRefillSubmit( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                _data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE
            };
        }
    }
}
